/**
 * bom.ts — pure BOM calculation function (MAT-01 / MAT-02 / CLAUDE.md rule 6).
 *
 * ROUNDING RULE (same as computeTotals.ts — Polish kosztorys convention):
 *   - quantity:  roundTo(norm * area * safetyFactor, 3)  — 3dp for material qty
 *   - lineTotal: roundTo(quantity * pricePerUnit, 2)     — 2dp for money
 *   - netTotal:  sum of already-rounded lineTotals (NOT rounded again)
 *
 * FORBIDDEN: Math.round / toFixed in the math path — only roundTo from shared/lib/number.
 * FORBIDDEN: import from @/entities/service or @/entities/estimate — FSD leaf rule.
 *
 * FSD BOUNDARY (BLOCKING): entities/material is a leaf entity.
 * MaterialNorm type is NOT imported from entities/service — a local structural
 * NormData interface is used instead (TypeScript structural typing = same shape, no dep).
 * The feature coordinator (useMaterials) bridges norms as plain NormData[] objects.
 *
 * safetyFactor contract: pass 1 + safetyMargin / PERCENT_DIVISOR (e.g. 1.10 for 10% waste).
 * Conversion happens in the feature hook — NOT inside buildBOM.
 * Guard: safetyFactor must be in [1, 5]. Values outside this range indicate
 * a caller passed safetyMargin (percent integer) instead of the multiplier.
 */

import { roundTo } from '@/shared/lib/number';
import type { RoomGeometry } from '@/shared/model';
import type { Material, BOMLine } from '../model/types';

// ---------------------------------------------------------------------------
// Local structural interface — mirrors MaterialNorm without importing it.
// TypeScript structural typing: NormData is assignment-compatible with
// MaterialNorm wherever MaterialNorm.surfaceType?: string is present.
// (FSD leaf rule: no import from entities/service or entities/estimate.)
// ---------------------------------------------------------------------------

interface NormData {
  materialId: string;
  norm: number;
  /** unitType key — absent defaults to 'pcs' in resolveArea */
  surfaceType?: string;
}

// ---------------------------------------------------------------------------
// Resolves the area value for a given surfaceType from RoomGeometry.
// Mirrors resolveUnitQuantity() in src/shared/lib/geometry.ts — same case labels.
// Local copy (not imported) per FSD leaf constraint.
// ---------------------------------------------------------------------------

function resolveArea(surfaceType: string, geom: RoomGeometry): number {
  switch (surfaceType) {
    case 'm2_floor':      return geom.floorArea;
    case 'm2_ceiling':    return geom.floorArea; // ceiling area = floor area (same as geometry.ts)
    case 'm2_wall':       return geom.wallArea;
    case 'm2':            return geom.wallArea;  // alias
    case 'mp':            return geom.perimeter;
    case 'mp_slopes_win': return geom.windowSlopes;
    case 'mp_slopes_door': return geom.doorSlopes;
    case 'pcs':           return 1; // norm applies once (manual count)
    default:              return 1; // safe fallback — pcs behaviour
  }
}

// ---------------------------------------------------------------------------
// buildBOM — pure function, no side effects, no React, no Zustand
// ---------------------------------------------------------------------------

/**
 * Builds a Bill of Materials from norms, a material catalog, room geometry,
 * and a waste safety factor.
 *
 * @param norms        - Norm data array (materialId + norm + optional surfaceType)
 * @param catalog      - Map<materialId, Material> from useMaterialStore
 * @param geometry     - Computed RoomGeometry (from calculateRoomGeometry)
 * @param safetyFactor - Waste multiplier ∈[1,5]; e.g. 1.10 for +10% waste
 *                       MUST be 1 + safetyMargin/100 — do NOT pass safetyMargin directly
 * @returns            - BOMLine[] filtered to known materials; unknown IDs silently skipped
 *
 * @throws {Error} if safetyFactor < 1 or > 5 (T-06-OVF mitigation)
 */
export function buildBOM(
  norms: NormData[],
  catalog: Map<string, Material>,
  geometry: RoomGeometry,
  safetyFactor: number,
): BOMLine[] {
  // T-06-OVF: Guard against safetyMargin-as-factor confusion (10 × quantities)
  if (safetyFactor < 1 || safetyFactor > 5) {
    throw new Error(
      `buildBOM: safetyFactor ${safetyFactor} is out of range [1, 5]. ` +
      `Did you pass safetyMargin (percent integer) instead of the multiplier? ` +
      `Use: safetyFactor = 1 + safetyMargin / 100`,
    );
  }

  const lines: (BOMLine | null)[] = norms.map((n) => {
    const material = catalog.get(n.materialId);
    // T-06-REF: unknown materialId → silently skip (estimate never crashes)
    if (!material) return null;

    const surfaceType = n.surfaceType ?? 'pcs';
    const area = resolveArea(surfaceType, geometry);
    const quantity = roundTo(n.norm * area * safetyFactor, 3);
    const lineTotal = roundTo(quantity * material.pricePerUnit, 2);

    return {
      materialId: n.materialId,
      material,
      norm: n.norm,
      surfaceType,
      area,
      safetyFactor,
      quantity,
      pricePerUnit: material.pricePerUnit,
      lineTotal,
      normIsManual: false,
      qtyIsManual: false,
    } satisfies BOMLine;
  });

  return lines.filter((l): l is BOMLine => l !== null);
}

// ---------------------------------------------------------------------------
// recomputeBOMLine — used by the feature layer to refresh a BOM line
// after geometry or safetyFactor changes while preserving manual overrides.
// ---------------------------------------------------------------------------

/**
 * Recomputes a single BOM line given new geometry and safetyFactor.
 *
 * Manual override contract:
 *  - If line.normIsManual === true → return line unchanged (user edited the norm).
 *  - If line.qtyIsManual === true  → return line unchanged (user typed qty directly).
 *  - Otherwise → recompute area / quantity / lineTotal from fresh geometry.
 *
 * @throws {Error} if safetyFactor out of [1, 5] (same guard as buildBOM)
 */
export function recomputeBOMLine(
  line: BOMLine,
  geometry: RoomGeometry,
  safetyFactor: number,
): BOMLine {
  // Preserve manual overrides — do NOT clobber user values
  if (line.normIsManual || line.qtyIsManual) return line;

  if (safetyFactor < 1 || safetyFactor > 5) {
    throw new Error(
      `recomputeBOMLine: safetyFactor ${safetyFactor} out of range [1, 5]`,
    );
  }

  const area = resolveArea(line.surfaceType, geometry);
  const quantity = roundTo(line.norm * area * safetyFactor, 3);
  const lineTotal = roundTo(quantity * line.pricePerUnit, 2);

  return { ...line, area, safetyFactor, quantity, lineTotal };
}
